import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, FileText, Inbox, Loader2, LogOut, Mail, Phone, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function withRefresh(fn) {
  try {
    return await fn();
  } catch (err) {
    if (err.response?.status === 401) {
      await api.post("/auth/refresh");
      return await fn();
    }
    throw err;
  }
}

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40";

const STATUSES = ["new", "read", "replied", "archived"];
const STATUS_STYLES = {
  new: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
  read: "border-white/15 bg-white/5 text-slate-300",
  replied: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  archived: "border-white/10 bg-white/5 text-slate-500",
};

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s || "-";
  }
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [e, q] = await withRefresh(() => Promise.all([api.get("/enquiries"), api.get("/quotes")]));
      setEnquiries(e.data.enquiries || []);
      setQuotes(q.data.quotes || []);
    } catch (err) {
      if (err.response?.status === 401) setUser(false);
      else toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", creds);
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      setLoginError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    toast.success("Signed out");
  };

  const updateStatus = async (id, status) => {
    try {
      await withRefresh(() => api.patch(`/enquiries/${id}/status`, { status }));
      setEnquiries((list) => list.map((x) => (x.enquiry_id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await withRefresh(() => api.delete(`/${kind}/${id}`));
      if (kind === "enquiries") setEnquiries((l) => l.filter((x) => x.enquiry_id !== id));
      else setQuotes((l) => l.filter((x) => x.quote_id !== id));
      toast.success("Record deleted");
    } catch {
      toast.error("Could not delete record");
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-4" data-testid="admin-login-page">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <div className="glass relative w-full max-w-sm rounded-2xl p-8" data-testid="admin-login-card">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-login-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-electric to-cyan-400">
              <span className="font-display text-lg font-black text-ink">T</span>
            </span>
            <span className="font-display text-lg font-bold text-white">Tech<span className="text-cyan-400">Axis</span></span>
          </Link>
          <h1 className="mt-6 font-display text-2xl font-bold text-white">Admin sign in</h1>
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5 text-cyan-400" /> Restricted to TechAxis staff
          </p>
          <form onSubmit={login} className="mt-6 space-y-4" data-testid="admin-login-form">
            <input type="email" required value={creds.email} onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))} placeholder="Admin email" className={inputCls} data-testid="admin-email-input" />
            <input type="password" required value={creds.password} onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))} placeholder="Password" className={inputCls} data-testid="admin-password-input" />
            {loginError && <p className="text-sm text-red-400" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loggingIn} className="w-full rounded-full bg-electric px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-400 hover:text-ink disabled:opacity-60" data-testid="admin-login-submit-button">
              {loggingIn ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const newCount = enquiries.filter((e) => e.status === "new").length;

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-electric to-cyan-400">
              <span className="font-display text-base font-black text-ink">T</span>
            </span>
            <span className="font-display font-bold text-white">TechAxis <span className="text-cyan-400">Admin</span></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-slate-400 sm:block" data-testid="admin-user-email">{user.email}</span>
            <button type="button" onClick={loadData} className="rounded-full border border-white/10 p-2 text-slate-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-300" data-testid="admin-refresh-button" aria-label="Refresh data">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <Link to="/" className="hidden rounded-full border border-white/10 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:text-cyan-300 sm:block" data-testid="admin-view-site-link">View site</Link>
            <button type="button" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-red-400/40 hover:text-red-300" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: "Total enquiries", value: enquiries.length, testid: "admin-stat-enquiries" },
            { label: "New enquiries", value: newCount, testid: "admin-stat-new" },
            { label: "Quote requests", value: quotes.length, testid: "admin-stat-quotes" },
            { label: "Pipeline value", value: `${gbp(quotes.reduce((s, q) => s + (q.estimate_low || 0), 0))}+`, testid: "admin-stat-pipeline" },
          ].map((s) => (
            <div key={s.label} className="glass rounded-xl p-5" data-testid={s.testid}>
              <p className="font-display text-2xl font-extrabold text-white">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-2 border-b border-white/10" data-testid="admin-tabs">
          {[
            { id: "enquiries", label: `Enquiries (${enquiries.length})`, icon: Inbox, testid: "admin-tab-enquiries" },
            { id: "quotes", label: `Quote requests (${quotes.length})`, icon: FileText, testid: "admin-tab-quotes" },
          ].map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={t.testid}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                tab === t.id ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"
              }`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <p className="py-12 text-center text-sm text-slate-500" data-testid="admin-enquiries-empty">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <article key={e.enquiry_id} className="rounded-2xl border border-white/10 bg-navy-900/60 p-6" data-testid={`enquiry-card-${e.enquiry_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-lg font-semibold text-white">{e.name}</h3>
                      <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`} data-testid={`enquiry-status-${e.enquiry_id}`}>
                        {e.status || "new"}
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-[11px] text-slate-500">{fmtDate(e.created_at)} · source: {e.source || "contact"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={e.status || "new"} onChange={(ev) => updateStatus(e.enquiry_id, ev.target.value)} data-testid={`enquiry-status-select-${e.enquiry_id}`}
                      className="rounded-lg border border-white/10 bg-navy-950 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-400/50">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button type="button" onClick={() => remove("enquiries", e.enquiry_id)} className="rounded-lg border border-white/10 p-2 text-slate-400 transition-colors hover:border-red-400/40 hover:text-red-300" data-testid={`enquiry-delete-${e.enquiry_id}`} aria-label="Delete enquiry">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-300">
                  <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-cyan-400" />{e.email}</span>
                  {e.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-cyan-400" />{e.phone}</span>}
                  {e.company && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-cyan-400" />{e.company}</span>}
                </div>
                {(e.service || e.budget) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {e.service && <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">{e.service}</span>}
                    {e.budget && <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">{e.budget}</span>}
                  </div>
                )}
                <p className="mt-4 rounded-lg bg-white/5 p-4 text-sm leading-relaxed text-slate-300">{e.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="mt-6 space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && <p className="py-12 text-center text-sm text-slate-500" data-testid="admin-quotes-empty">No quote requests yet.</p>}
            {quotes.map((q) => (
              <article key={q.quote_id} className="rounded-2xl border border-white/10 bg-navy-900/60 p-6" data-testid={`quote-card-${q.quote_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-lg font-semibold text-white">{q.name}</h3>
                      <span className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-cyan-300">{q.quote_id}</span>
                    </div>
                    <p className="mt-1 font-mono text-[11px] text-slate-500">{fmtDate(q.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-display text-lg font-bold text-gradient" data-testid={`quote-estimate-${q.quote_id}`}>
                      {gbp(q.estimate_low)} – {gbp(q.estimate_high)}
                    </p>
                    <button type="button" onClick={() => remove("quotes", q.quote_id)} className="rounded-lg border border-white/10 p-2 text-slate-400 transition-colors hover:border-red-400/40 hover:text-red-300" data-testid={`quote-delete-${q.quote_id}`} aria-label="Delete quote">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-300">
                  <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-cyan-400" />{q.email}</span>
                  {q.company && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-cyan-400" />{q.company}</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-electric/15 px-3 py-1 text-xs font-medium text-blue-300">{(q.project_type || "").replace(/_/g, " ")}</span>
                  {(q.features || []).map((f) => <span key={f} className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">{f}</span>)}
                  <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">{q.timeline}</span>
                  <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">{q.support} support</span>
                </div>
                {q.notes && <p className="mt-4 rounded-lg bg-white/5 p-4 text-sm leading-relaxed text-slate-300">{q.notes}</p>}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
